
import { Input } from "@/components/ui/input";
import CustomerClassField from "./CustomerClassField";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { DataTable } from "@/components/ui/data-table";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { legalPersonCompanyName } from "../utilities/legalPersonDisplay";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Calendar as CalendarIcon,
    Layers,
    Link2Off,
    Loader2,
    MapPin,
    Plus,
    RefreshCw,
    Router,
    Trash2,
} from "lucide-react";
import type { Column, ColumnDef } from "@tanstack/react-table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { useEffect, useId, useMemo, useState, useCallback, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { userAPI } from "../UserAPI";
import InfoList from "./InfoList";
import DinPropertySheet from "./DinPropertySheet";

import {
    EntityQuery,
    LegalPerson,
    LinkedEntityQuery,
    ManagedFile,
    Product,
    Query,
    RelationshipDirection,
    Document,
    is,
    or,
    ProductApproval,
    MeasurementTypeI18N,
    Floor,
    Building,
    from,
} from "@the-real-insight/tri-model";
import { authenticatedFetch } from "../JWTAuthService";
import { configuration } from "../Configuration";
import AssetList from "../components/AssetList";
import {
  lifecyclePhaseAnnotations,
  lifecyclePhaseRanges,
  measurementAssetKeys,
  telemetryChartQuery,
  telemetryEventQuery,
  toChartSeries,
  meterDeviceType,
} from "./measurementSeries";
import MeasurementChartList from "./MeasurementChartList";
import {
  RANGE_OPTION_KEYS,
  useMeasurementRange,
  type MeasurementRangeKey,
} from "./measurementRange";
import i18next from "i18next";
import dayjs from "dayjs";
import { useCustomerTheme } from "../Theme";
import { useLocation, useNavigate } from "react-router-dom";
import { useAdministrationAPI } from "../AdministrationAPI";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import IoTDeviceTypeProperties from "./IoTDeviceTypeProperties";
import { useModalContext } from "../components/ModalProvider";
import AssetPhotosEditor from "../components/EntityPhotoListEditor";
import type { EntityPhoto } from "../components/EntityPhotoListEditor";
import EntityDocumentsPanel from "../components/EntityDocumentsPanel";
import AssetObligationsPanel from "./AssetObligationsPanel";
import AssetPartHierarchyPanel from "./AssetPartHierarchyPanel";
import AssetLifecyclePanel from "./lifecycle/AssetLifecyclePanel";
import AssetStatusHeader from "./AssetStatusHeader";
import { productPropertyValue } from "../utils/productPropertyValue";
import { AssetCard, DefinitionList, LocationChip } from "./AssetGeneralCards";
import {
  isOverdue,
  type LatestOperatingState,
  lifecycleRows,
  resolveAssetStatus,
  sendIntervalSeconds,
} from "./assetStatus";

/** API may return `Date` instances or serialized ISO strings. */
function formatAssetDateTime(value: unknown): string {
  if (value == null || value === "") {
    return "-";
  }
  const date =
    value instanceof Date ? value : new Date(value as string | number);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
}

export const ASSET_TAB_KEYS = [
  "general",
  "measurements",
  "product",
  "partHierarchy",
  "photos",
  "documents",
  "operatorObligations",
  "lifecycle",
] as const;

const DEFAULT_ASSET_TAB = ASSET_TAB_KEYS[0];

function resolveAssetTab(tabFromUrl: string | null): string {
  return tabFromUrl && (ASSET_TAB_KEYS as readonly string[]).includes(tabFromUrl)
    ? tabFromUrl
    : DEFAULT_ASSET_TAB;
}

class Properties {
  asset: any;
}

/** Klickbarer Spaltenkopf — ersetzt die `sortable`-Köpfe der abgelösten Tabelle. */
function SortableHeader({
  column,
  children,
}: {
  column: Column<any, unknown>;
  children: ReactNode;
}) {
  const sorted = column.getIsSorted();

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

/**
 * Datumsfeld wie der frühere Fluent-`DatePicker`: schreibgeschütztes Feld mit
 * Kalender-Popover. Die Anzeige formatiert der Aufrufer (`formatDate`).
 */
function DateField({
  value,
  disabled,
  formatDate,
  onSelectDate,
  ariaLabel,
}: {
  value?: Date;
  disabled?: boolean;
  formatDate: (date: Date | undefined) => string;
  onSelectDate: (date: Date | null | undefined) => void;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative w-[150px] max-w-full">
          <Input
            type="text"
            role="combobox"
            aria-label={ariaLabel}
            aria-haspopup="dialog"
            aria-expanded={open}
            value={value ? formatDate(value) : ""}
            disabled={disabled}
            readOnly
            onChange={() => {}}
            onClick={() => !disabled && setOpen((v) => !v)}
            className={disabled ? "pr-9" : "cursor-pointer pr-9"}
          />
          <CalendarIcon
            size={16}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          defaultMonth={value}
          onSelect={(date: Date | undefined) => {
            onSelectDate(date ?? null);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** Marketplace page of an offer (without voucher). */
const MARKETPLACE_URL = "https://www.real-gain.com/marketplace";

/** What a manufacturer sees in place of a feature it has not bought: a note and the offer. */
function MarketplaceNotice({ text, offer }: { text: string; offer?: string }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
      <p className="m-0">{text}</p>
      {offer && (
        <a
          className="mt-2 inline-block font-medium text-primary underline"
          href={`${MARKETPLACE_URL}/${offer}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("assetPropertiesPanel.openMarketplace")}
        </a>
      )}
    </div>
  );
}

export default function AssetProperties(properties: Properties) {
  const { t } = useTranslation();
  const { theme } = useCustomerTheme();
  const navigate = useNavigate();
  const { pushError } = useModalContext() as any;
  const { updateAsset: saveAsset } = useAdministrationAPI();
  // A manufacturer opens the assets of its products in other tenants read-only (server:
  // manufacturerAccess.ts) — no save is sent, the server would refuse it anyway.
  const manufacturerView = !!properties.asset?._manufacturerView;
  const manufacturerEntitlements = properties.asset?._manufacturerEntitlements;
  const updateAsset = useCallback(
    async (next: any) => (manufacturerView ? next : saveAsset(next)),
    [manufacturerView, saveAsset],
  );
  const [deviceType, setDeviceType] = useState() as any;
  // `undefined` while loading, `null` when the server knows no operating state (#1016).
  const [latestOperatingState, setLatestOperatingState] = useState<
    LatestOperatingState | undefined
  >(undefined);
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [tab, setTab] = useState(() => resolveAssetTab(searchParams.get("tab")));

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    const resolved = resolveAssetTab(tabFromUrl);
    if (tabFromUrl && tabFromUrl !== resolved) {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", resolved);
      window.history.replaceState(null, "", url.toString());
    }
    setTab((current) => (current === resolved ? current : resolved));
  }, [searchParams]);
  const [asset, setAsset] = useState(properties.asset) as any;
  // Inline-edited fields on the General tab. Local while typing, persisted on
  // blur so we don't write on every keystroke.
  const [generalProperties, setGeneralProperties] = useState<any>(
    properties.asset?.properties || {},
  );
  const [editName, setEditName] = useState<string>(properties.asset?.name ?? "");
  const [editDetails, setEditDetails] = useState<string>(properties.asset?.details ?? "");
  const [editIotConfig, setEditIotConfig] = useState<any>(
    properties.asset?.iotConfiguration || {},
  );
  const [product, setProduct] = useState() as any;
  const [productManufacturer, setProductManufacturer] = useState<any>(undefined);
  const [documents, setDocuments] = useState([]) as any;
  // Product picker dialog (assign or change the asset's product)
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [pickerProducts, setPickerProducts] = useState<any[]>([]);
  const [pickerManufacturers, setPickerManufacturers] = useState<Record<string, any>>({});
  const [pickerFilter, setPickerFilter] = useState("");
  const [_address, _setAddress] = useState() as any;
  const [building, setBuilding] = useState() as any;
  const [seriesList, setSeriesList] = useState<any>({});
  const [optionsList, setOptionsList] = useState<any>({});
  const [floor, setFloor] = useState() as any;
  const assetRef = useRef<any>(asset);
  const viewedAssetIdRef = useRef(properties.asset?._id);
  const nameFieldId = useId();
  const detailsFieldId = useId();
  const pickerFilterId = useId();

  // TODO Move to component

  const {
    range: interval,
    start: intervalStart,
    end: intervalEnd,
    autoWidenedTo,
    select: selectInterval,
    setCustomStart,
    setCustomEnd,
    reportData,
    reset: resetRange,
  } = useMeasurementRange();
  const [measurementsLoading, setMeasurementsLoading] = useState(false);
  const [timeUntilNextUpdate, setTimeUntilNextUpdate] = useState(30);
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(true);
  const callRef = useRef<() => Promise<void>>();
  const timerRef = useRef<number | null>(null);

  // Picker: load all Products when the dialog opens, filter client-side.
  useEffect(() => {
    if (!productPickerOpen) return;
    const fetch = async () => {
      try {
        const rows = await userAPI.query(new Query(new EntityQuery(Product.type)));
        const list = Array.isArray(rows) ? rows : [];
        setPickerProducts(list);

        // post-migration manufacturer is just an id ref; batch-resolve so the
        // picker column and filter can display/match on companyName.
        const ids = Array.from(
          new Set(
            list
              .map((p: any) => p?.manufacturer?._id)
              .filter((id: any) => typeof id === "string" && id.length > 0),
          ),
        );
        if (ids.length > 0) {
          const lpRows = await userAPI.query(
            new Query(
              new EntityQuery(
                LegalPerson.type,
                [],
                undefined,
                or(...ids.map((id: any) => is("_id").equal(id))),
              ),
            ),
          );
          const next: Record<string, any> = {};
          for (const lp of lpRows || []) {
            if (lp?._id) next[String(lp._id)] = lp;
          }
          setPickerManufacturers(next);
        } else {
          setPickerManufacturers({});
        }
      } catch (err) {
        pushError(err);
      }
    };
    fetch();
  }, [productPickerOpen]);

  const resolvedPickerManufacturer = (product: any) => {
    const id = product?.manufacturer?._id
      ? String(product.manufacturer._id)
      : "";
    return (id && pickerManufacturers[id]) || product?.manufacturer;
  };

  const filteredPickerProducts = useMemo(() => {
    const q = pickerFilter.trim().toLowerCase();
    if (!q) return pickerProducts;
    return pickerProducts.filter((p: any) => {
      const mfg = legalPersonCompanyName(resolvedPickerManufacturer(p)) || "";
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.schemaCategory || "").toLowerCase().includes(q) ||
        mfg.toLowerCase().includes(q)
      );
    });
  }, [pickerProducts, pickerFilter, pickerManufacturers]);

  const assignProductToAsset = useCallback(
    async (newProduct: any) => {
      try {
        const productId = newProduct?._id ? String(newProduct._id) : "";
        if (!productId) return;
        const updated = await updateAsset({ ...asset, product: productId });
        setAsset(updated);
        setProduct(newProduct);
        setProductPickerOpen(false);
        setPickerFilter("");
      } catch (err) {
        pushError(err);
      }
    },
    [asset, updateAsset, pushError],
  );

  const clearAssetProduct = useCallback(async () => {
    try {
      const next: any = { ...asset };
      // Send empty string so the backend overwrites (some serializers skip undefined).
      next.product = "";
      const updated = await updateAsset(next);
      setAsset(updated);
      setProduct(undefined);
      setProductManufacturer(undefined);
      setDocuments([]);
    } catch (err) {
      pushError(err);
    }
  }, [asset, updateAsset, pushError]);

  const productPickerColumns: ColumnDef<any, any>[] = [
    {
      id: "product",
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t("assetPropertiesPanel.productPicker.product")}
        </SortableHeader>
      ),
      accessorFn: (p: any) => p.name || "",
      cell: ({ row }) => <span>{row.original.name || "-"}</span>,
      size: 320,
    },
    {
      id: "category",
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t("assetPropertiesPanel.productPicker.category")}
        </SortableHeader>
      ),
      accessorFn: (p: any) => p.schemaCategory || "",
      cell: ({ row }) => <span>{row.original.schemaCategory || "-"}</span>,
      size: 180,
    },
    {
      id: "manufacturer",
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t("assetPropertiesPanel.productPicker.manufacturer")}
        </SortableHeader>
      ),
      accessorFn: (p: any) =>
        legalPersonCompanyName(resolvedPickerManufacturer(p)) || "",
      cell: ({ row }) => (
        <span>
          {legalPersonCompanyName(resolvedPickerManufacturer(row.original)) || "-"}
        </span>
      ),
      size: 200,
    },
  ];

  // Open via authenticated blob fetch for /managed-files/<id> (those need JWT and
  // also prefer the inline content-type), fall back to a plain link otherwise.
  const openProductDocument = useCallback(
    async (url: string) => {
      try {
        if (!url) return;
        if (!url.startsWith("/managed-files/")) {
          window.open(`${configuration.serverUrl}${url}`, "_blank");
          return;
        }
        const response = await authenticatedFetch(
          `${configuration.serverUrl}${url}`,
          { method: "GET", headers: { Accept: "*/*" } },
        );
        if (!response.ok) {
          throw new Error(`Failed to open document (${response.status})`);
        }
        const blob = await response.blob();
        const contentType =
          response.headers.get("content-type") || "application/octet-stream";
        const blobUrl = URL.createObjectURL(
          new Blob([blob], { type: contentType }),
        );
        window.open(blobUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } catch (err) {
        pushError(err);
      }
    },
    [pushError],
  );

  const persistAssetPhotos = useCallback(
    async (photos: EntityPhoto[]) => {
      const currentAsset = assetRef.current;
      if (!currentAsset?._id) return;
      const savedId = currentAsset._id;
      try {
        const updated = await updateAsset({ ...currentAsset, photos });
        if (String(viewedAssetIdRef.current) !== String(savedId)) return;
        setAsset(updated);
      } catch (e) {
        pushError(e);
      }
    },
    [pushError, updateAsset],
  );

  // When the parent passes a different asset (e.g. URL entity change), reset local
  // state and keep assetRef aligned so blur persistence targets the right record.
  useEffect(() => {
    const nextAsset = properties.asset;
    const id = nextAsset?._id;
    if (!id) {
      return;
    }
    viewedAssetIdRef.current = id;
    assetRef.current = nextAsset;
    setAsset(nextAsset);
    setGeneralProperties(nextAsset?.properties || {});
    setEditName(nextAsset?.name ?? "");
    setEditDetails(nextAsset?.details ?? "");
    setEditIotConfig(nextAsset?.iotConfiguration || {});
    resetRange();
  }, [properties.asset?._id]);

  // Keep assetRef in sync with the latest asset state
  useEffect(() => {
    assetRef.current = asset;
  }, [asset]);

  // Write a patch through to the server, but only if something actually changed.
  const persistAssetPatch = useCallback(
    async (patch: Record<string, any>) => {
      const currentAsset = assetRef.current;
      if (!currentAsset?._id) return;
      const changed = Object.keys(patch).some(
        (k) => JSON.stringify(patch[k]) !== JSON.stringify((currentAsset as any)[k]),
      );
      if (!changed) return;
      const savedId = currentAsset._id;
      try {
        const updated = await updateAsset({ ...currentAsset, ...patch });
        if (String(viewedAssetIdRef.current) !== String(savedId)) return;
        setAsset(updated);
      } catch (e) {
        pushError(e);
      }
    },
    [pushError, updateAsset],
  );

  /**
   * Eigenschaften sichern — und den Qualitätszustand **zusätzlich** ins eigene Feld schreiben.
   *
   * Der Zustand lag bisher nur als Schlüssel im freien `properties`-Bündel. Seit tri-model 0.0.861
   * ist `Asset.qualityState` ein deklariertes Feld mit Wertemenge und Beschriftung — dadurch findet
   * es die Objektsuche, `describe_type` nennt es, und ein Tippfehler fällt beim Schreiben auf statt
   * eine eigene Gruppe zu bilden.
   *
   * **Übergangsweise beides.** Solange die Altdaten noch im Bündel liegen und die Auswertung dort
   * liest, wäre ein harter Umstieg eine Reihenfolgefalle: Wer zuerst umzieht, verliert entweder die
   * neuen oder die alten Einträge. Doppelt zu schreiben kostet drei Zeilen und macht die
   * Reihenfolge des Ausrollens egal. Der Bündeleintrag fällt, wenn die Migration durch ist.
   */
  const persistAssetProperties = useCallback(
    (nextProperties: any) => {
      const qualityState = nextProperties?.qualityState;
      return persistAssetPatch({
        properties: nextProperties,
        ...(qualityState !== undefined ? { qualityState } : {}),
      });
    },
    [persistAssetPatch],
  );

  // Deliberately does not touch `measurementsLoading`: these run concurrently, and
  // the first one to finish would otherwise clear the flag while the rest are
  // still in flight. `call` owns the flag for the whole batch.
  const queryTelemetry = useCallback(
    async (params: any) => userAPI.queryTelemetry(params),
    [],
  );

  // Create a stable call function using useCallback with minimal dependencies
  const call = useCallback(async () => {
    if (!deviceType || !asset) return;

    setMeasurementsLoading(true);

    try {
      // Get current values at the time of execution
      const currentIntervalStart = intervalStart;
      const currentIntervalEnd = intervalEnd;

      // Metrics
      const newSeriesList: any = {};
      const newOptionsList: any = {};
      // Concrete CI hex values (NOT currentTheme.*): the theme colors resolve to
      // CSS-variable / rgb() strings, which ApexCharts' color parser cannot
      // handle — with a gradient fill it recurses on the bad color and throws
      // "Maximum call stack size exceeded".
      const chartColors = ["#5F3A79", "#98AC4D", "#001A61"];
      let index = 0;

      // Only fetch measurements inside the displayed interval. Without this the
      // server returns the asset's entire telemetry history per metric (full
      // result set), which is the dominant cost even with the asset+metric
      // index. The server coerces these bounds to Date and the
      // {asset, metric, timestamp} index serves the range directly.
      //
      // The metric reads also carry `maxPoints` (#738): a wide interval holds
      // far more raw readings than a 400px chart can show, and the server folds
      // them into that many time buckets instead of sending every row. Events
      // stay raw — each occurrence becomes its own annotation.
      const timeWindow = { start: currentIntervalStart, end: currentIntervalEnd };
      // Under both keys the device's readings may be stored (#1016) — see measurementSeries.
      const assetKeys = measurementAssetKeys(asset);

      // One read per metric, plus one per event that annotates a chart. They are
      // independent, so they go out together: issued in sequence they cost thirty
      // round trips before the first chart can draw, which is what made this tab
      // slow no matter how short the interval — the queries themselves are served
      // from {asset, metric, timestamp} in single-digit milliseconds.
      //
      // Events are fetched once each, not once per related metric: temperatureAlarm
      // annotates both temperature charts, and asking for it twice returns the same
      // rows twice.
      const relatedEvents = deviceType.events.filter((event: any) =>
        deviceType.state.some(
          (field: any) => event.relatedState.indexOf(field.id) >= 0,
        ),
      );

      const [metricRows, eventRows] = await Promise.all([
        Promise.all(
          deviceType.state.map((field: any) =>
            queryTelemetry(telemetryChartQuery(field.id, assetKeys, timeWindow)),
          ),
        ),
        Promise.all(
          relatedEvents.map((event: any) =>
            queryTelemetry(telemetryEventQuery(event.id, assetKeys, timeWindow)),
          ),
        ),
      ]);

      // No reading at all in an automatically chosen range: widen it (30 -> 90 -> 365 days -> all).
      // The range change reloads; do not draw the empty result in between.
      const anyData = metricRows.some((rows: any) => Array.isArray(rows) && rows.length > 0);
      if (reportData(anyData)) {
        return;
      }

      const seriesByMetric = new Map<string, any[]>(
        deviceType.state.map((field: any, i: number) => [field.id, metricRows[i]]),
      );
      const occurrencesByEvent = new Map<string, any[]>(
        relatedEvents.map((event: any, i: number) => [event.id, eventRows[i]]),
      );

      for (const field of deviceType.state) {
        newSeriesList[field.id] = toChartSeries(
          field.id,
          seriesByMetric.get(field.id) ?? [],
        );

        const events = [];
        let annotations: any = {
          // Werks- und Inbetriebnahme-Werte (tri-server #1220) bleiben sichtbar, aber als
          // schattierter Bereich „Werk“ / „Inbetriebnahme“ markiert.
          xaxis: lifecyclePhaseAnnotations(
            lifecyclePhaseRanges(seriesByMetric.get(field.id) ?? []),
            {
              factory: t("assetPropertiesPanel.lifecyclePhase.factory"),
              commissioning: t("assetPropertiesPanel.lifecyclePhase.commissioning"),
            },
          ),
          points: [],
        };

        for (const event of deviceType.events) {
          if (event.relatedState.indexOf(field.id) >= 0) {
            events.push(event.id);

            const occurrences = occurrencesByEvent.get(event.id) ?? [];

            for (const occurrence of occurrences) {
              annotations.xaxis.push({
                x: dayjs(occurrence.timestamp).toDate().getTime(),
                strokeDashArray: 0,
                borderColor: "#AA0000",
                label: {
                  borderColor: "#AA0000",
                  style: {
                    color: "#fff",
                    background: "#AA0000",
                  },
                  text: MeasurementTypeI18N[i18next.language][
                    occurrence.metric
                  ],
                },
              });
            }
          }
        }

        if (field.id === "pressure") {
          annotations.yaxis = [
            {
              y: 0.8,
              borderColor: "#AA0000",
              label: {
                borderColor: "#AA0000",
                style: {
                  color: "#fff",
                  background: "#AA0000",
                },
                text: "Low Pressure",
              },
            },
          ];
        }

        newOptionsList[field.id] = {
          chart: {
            height: 350,
            type: "area",
          },
          dataLabels: {
            enabled: false,
          },
          colors: [chartColors[index % 3]],
          stroke: {
            width: [1, 1],
          },
          xaxis: {
            type: "datetime",
            min: currentIntervalStart.getTime(),
            max: currentIntervalEnd.getTime(),
          },
          yaxis: [
            {
              seriesName: field.id,
              min: 0,
              axisTicks: {
                show: true,
              },
              axisBorder: {
                show: true,
              },
              labels: {
                formatter: (val: number) => {
                  return val ? val.toFixed(1) : "";
                },
              },
              title: {
                text: field.unit ? ` ${field.unit}` : "",
              },
            },
          ],
          tooltip: {
            x: {
              format: "dd/MM/yy HH:mm",
            },
          },
          fill: {
            type: "gradient",
            gradient: {
              shadeIntensity: 1,
              opacityFrom: 0.7,
              opacityTo: 0.9,
              stops: [0, 100],
            },
          },
          annotations,
        };

        ++index;
      }

      setSeriesList(newSeriesList);
      setOptionsList(newOptionsList);
    } finally {
      setMeasurementsLoading(false);
    }
  }, [asset, deviceType, intervalEnd, intervalStart, queryTelemetry, reportData, theme, t]);

  // Update the ref whenever call function changes
  useEffect(() => {
    callRef.current = call;
  }, [call]);

  // Auto-update functionality
  const startAutoUpdate = () => {
    setTimeUntilNextUpdate(30);
    setAutoUpdateEnabled(true);

    // Clear existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Start new timer
    timerRef.current = window.setInterval(() => {
      setTimeUntilNextUpdate((prev) => {
        if (prev <= 1) {
          if (callRef.current) {
            callRef.current();
          }
          return 30;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopAutoUpdate = () => {
    setAutoUpdateEnabled(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Without this the interval kept fetching (and setting state) after the user left the page.
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    },
    [],
  );

  const forceUpdate = () => {
    if (deviceType) {
      call();
      startAutoUpdate();
    }
  };

  // Initial call when device type changes
  useEffect(() => {
    if (!deviceType) {
      return;
    }

    if (callRef.current) {
      callRef.current();
    }
    startAutoUpdate();
  }, [deviceType]);

  // Handle interval changes only (theme is stable)
  useEffect(() => {
    if (!deviceType) {
      return;
    }

    if (callRef.current) {
      callRef.current();
    }
    startAutoUpdate();
  }, [deviceType, intervalStart, intervalEnd]);

  useEffect(() => {
    if (!asset) {
      return;
    }

    let cancelled = false;

    const call = async () => {
      try {
        console.log("Asset >>>", asset);

        if (asset.product) {
          let result = await userAPI.query(
            new Query(
              new EntityQuery(
                Product.type,
                [],
                undefined,
                is("_id").equal(asset.product),
              ),
            ),
          );

          if (result && result.length === 1) {
            setProduct(result[0]);
          }

          // Fetch the full LegalPerson for the manufacturer so we can show its address.
          // The embedded `product.manufacturer` from our crawler only carries _id +
          // companyName + city; the LegalPerson record has street, postalCode, etc.
          const mfgRef: any = result && result.length === 1 && result[0].manufacturer;
          const mfgId = mfgRef?._id ? String(mfgRef._id) : "";
          if (mfgId) {
            try {
              const lpRows = await userAPI.query(
                new Query(
                  new EntityQuery(
                    LegalPerson.type,
                    [],
                    undefined,
                    is("_id").equal(mfgId),
                  ),
                ),
              );
              if (Array.isArray(lpRows) && lpRows.length > 0) {
                setProductManufacturer(lpRows[0]);
              } else {
                setProductManufacturer(mfgRef);
              }
            } catch (e) {
              setProductManufacturer(mfgRef);
            }
          } else {
            setProductManufacturer(undefined);
          }

          // Fetch ManagedFile docs referenced by Product.documents (added by the
          // crawler import) — one per-id query so each goes through tri-server's
          // _id-direct-lookup path (an OR on _id strings would not coerce to ObjectId).
          const mfIds: string[] = (
            (result && result.length === 1 && result[0].documents) || []
          )
            .map((id: any) => (id ? String(id) : ""))
            .filter(Boolean);
          let managedFileDocs: any[] = [];
          if (mfIds.length > 0) {
            const fetched = await Promise.all(
              mfIds.map(async (id: string) => {
                const rows = await userAPI.query(
                  new Query(
                    new EntityQuery(
                      ManagedFile.type,
                      [],
                      undefined,
                      is("_id").equal(id),
                    ),
                  ),
                );
                return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
              }),
            );
            managedFileDocs = fetched.filter(Boolean).map((mf: any) => {
              const segs = (mf.path || "").split("/").filter(Boolean);
              const filename = segs[segs.length - 1] || "document";
              let decodedFilename: string;
              try {
                decodedFilename = decodeURIComponent(filename);
              } catch {
                decodedFilename = filename;
              }
              const role =
                (Array.isArray(mf.roles) && mf.roles[0]) ||
                segs[segs.length - 2] ||
                "other";
              const known = new Set([
                "datasheet",
                "manual",
                "certificate",
                "approval",
                "drawing",
                "other",
              ]);
              const r = known.has(String(role).toLowerCase())
                ? String(role).toLowerCase()
                : "other";
              const i18nKey = `settingsPanel.productManagementPanel.role${r.charAt(0).toUpperCase()}${r.slice(1)}`;
              return {
                type: t(i18nKey),
                synopsis: decodedFilename,
                url: `/managed-files/${mf._id}`,
                summary: (mf as any).summary || "",
              };
            });
          }

          // Approvals are a nice-to-have on the documents tab. This query has to be
          // allowed to fail on its own: it used to share the surrounding try with
          // everything below it, so a 500 here skipped the IoT device type as well and
          // the measurements tab reported "No IoT device type set" for a device that
          // had one.
          try {
            result = await userAPI.query(
              new Query(
                new EntityQuery(
                  Product.type,
                  [],
                  undefined,
                  is("_id").equal(asset.product),
                ),
                [
                  new LinkedEntityQuery(
                    "approvals",
                    RelationshipDirection.out,
                    ProductApproval.type,
                  ),
                  new LinkedEntityQuery(
                    "notification",
                    RelationshipDirection.out,
                    Document.type,
                  ),
                ],
              ),
            );
          } catch (e) {
            // `result` is typed from the query above, so the empty list — not null —
            // is what says "no approvals came back". The check below reads it the
            // same way either way.
            result = [];
          }

          const approvalDocs =
            result && result.length === 1
              ? (result[0].approvals ?? [])
                  // An approval without its notification document carries no link to
                  // show, and reading through it would throw here.
                  .filter((approval: any) => approval?.notification)
                  .map((approval: any) => ({
                    type: "Device Approval",
                    synopsis: approval.notification.synopsis,
                    url: approval.notification.url,
                  }))
              : [];
          setDocuments([...managedFileDocs, ...approvalDocs]);
        }

        if (asset.iotDeviceType) {
          const newCatalog = await userAPI.getDeviceTypeCatalog();

          const deviceTypePath = asset.iotDeviceType.split("/");
          const newDeviceType =
            newCatalog[deviceTypePath[0]][deviceTypePath[1]];

          // TODO Add to library

          newDeviceType.id = asset.iotDeviceType;

          console.log("Device type >>>", newDeviceType);

          setDeviceType(newDeviceType);
        } else if (asset.meterMedium) {
          // Zähler ohne IoT-Gerät: die Zählerstände als Rohdaten, wie bei einem Gerät.
          setDeviceType(meterDeviceType(asset.meterMedium));
        }

        // The manufacturer cannot see the owner's building by address (tenant scope), only by
        // the asset's building reference (`_id` lookups cross tenants).
        const buildings = await userAPI.query(
          from(Building.type).where(
            manufacturerView && asset.building
              ? is("_id").equal(String(asset.building))
              : is("id").equal(
                  `${asset.street} ${asset.streetNumber}, ${asset.postalCode} ${asset.city}, ${asset.country}`,
                ),
          ),
        );

        console.log("Buildings >>>", buildings);

        if (buildings && buildings.length === 1) {
          setBuilding(buildings[0]);
        }

        if (asset.floor) {
          const floor = await userAPI.queryOne(from(Floor.type).where(is("_id").equal(asset.floor)));

          if (cancelled) return;

          if (floor !== null) {
            setFloor(floor);
          }
        }
      } catch (error) {
        // TODO Process error

        console.log(error);
      }
    };

    call();

    return () => {
      cancelled = true;
    };
  }, [asset, manufacturerView]);

  // The latest operating state; the server already folds in maintenance and decommissioning.
  useEffect(() => {
    const key = asset?.id ?? asset?._id;
    if (!key) return;
    let cancelled = false;
    setLatestOperatingState(undefined);
    (async () => {
      try {
        const response = await authenticatedFetch(
          `${configuration.serverUrl}/assets/${encodeURIComponent(String(key))}/operating-states/latest`,
          { method: "GET", headers: { Accept: "application/json" } },
        );
        if (!response.ok) throw new Error(`Failed to load status (${response.status})`);
        const body = await response.json();
        if (!cancelled) setLatestOperatingState(body ?? null);
      } catch (error) {
        // Failure shows as "unknown" below rather than as a spinner that never ends.
        console.log(error);
        if (!cancelled) setLatestOperatingState({ value: -1 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [asset?.id, asset?._id, asset?.assetState, asset?.maintenanceSince]);

  const assetStatus = useMemo(
    () =>
      latestOperatingState === undefined
        ? null
        : resolveAssetStatus({
            asset,
            operatingState: latestOperatingState,
            sendIntervalSeconds: sendIntervalSeconds(asset, deviceType),
          }),
    [asset, deviceType, latestOperatingState],
  );

  const selectTab = (value: string) => {
    const url = new URL(window.location.href);

    url.searchParams.set("tab", value);
    window.history.replaceState(null, "", url.toString());

    setTab(value);
  };

  // The address fields can be missing or stored as null; only join what is there.
  const addressText = [
    [asset?.street, asset?.streetNumber],
    [asset?.postalCode, asset?.city],
  ]
    .map((part) => part.filter((value) => value != null && value !== "").join(" "))
    .filter(Boolean)
    .join(", ");

  return asset ? (
    <>
      {manufacturerView && (
        <div className="mb-3 rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
          {t("assetPropertiesPanel.manufacturerView")}
        </div>
      )}
      <div className="entityFillColumn">
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{t("assetPropertiesPanel.asset")}</p>
          <h2 className="mt-0.5 break-words text-xl font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))] sm:text-2xl">
            {asset.name ? `${asset.id}/${asset.name}` : asset.id}
          </h2>
          <AssetStatusHeader status={assetStatus} />
          <nav
            aria-label={t("assetPropertiesPanel.locationLinks")}
            className="mt-3 flex min-w-0 flex-wrap items-center gap-2"
          >
            {addressText ? (
              building ? (
                <LocationChip
                  icon={<MapPin aria-hidden="true" className="h-4 w-4" />}
                  label={addressText}
                  onClick={() => navigate(`/entity?selectedEntityType=Building&selectedEntity=${building._id}`)}
                />
              ) : (
                <span className="inline-flex min-w-0 items-center gap-1.5 break-words text-sm text-muted-foreground">
                  <MapPin aria-hidden="true" className="h-4 w-4 shrink-0" />
                  {addressText}
                </span>
              )
            ) : null}
            {floor ? (
              <LocationChip
                icon={<Layers aria-hidden="true" className="h-4 w-4" />}
                label={floor.name}
                onClick={() => navigate(`/entity?selectedEntityType=Floor&selectedEntity=${floor._id}`)}
              />
            ) : null}
            {asset.iotGateway ? (
              <LocationChip
                icon={<Router aria-hidden="true" className="h-4 w-4" />}
                label={t("assetPropertiesPanel.gateway")}
                onClick={() => navigate(`/settings?panel=telemetry&tab=0&gateway=${asset.iotGateway}`)}
              />
            ) : null}
          </nav>
        </div>
        <div className="mt-4 entityFillColumn">
          <Tabs value={String(tab)} onValueChange={selectTab} className="entityFillColumn">
            <TabsList className="max-sm:[&_[role=tab]]:min-h-[44px]">
              <TabsTrigger value="general">
                {t("assetPropertiesPanel.general")}
              </TabsTrigger>
              <TabsTrigger value="measurements">
                {t("assetPropertiesPanel.measurements")}
              </TabsTrigger>
              <TabsTrigger value="product">
                {t("assetPropertiesPanel.product")}
              </TabsTrigger>
              <TabsTrigger value="partHierarchy">
                {t("assetPropertiesPanel.partHierarchy", "Teilehierarchie")}
              </TabsTrigger>
              <TabsTrigger value="photos">
                {t("assetPropertiesPanel.photos")}
              </TabsTrigger>
              <TabsTrigger value="documents">
                {t("assetPropertiesPanel.documents.tab")}
              </TabsTrigger>
              <TabsTrigger value="operatorObligations">
                {t("assetPropertiesPanel.operatorObligations")}
              </TabsTrigger>
              <TabsTrigger value="lifecycle">
                {t("assetPropertiesPanel.lifecycle.tab")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="general" className="mt-4">
              <div className="grid min-w-0 items-stretch gap-4 lg:grid-cols-2">
                <AssetCard
                  title={t("assetPropertiesPanel.basics")}
                  testId="asset-card-basics"
                >
                  <div className="flex min-w-0 flex-col gap-3">
                    <div className="flex min-w-0 flex-col gap-1.5">
                      <Label htmlFor={nameFieldId}>{t("assetPropertiesPanel.name")}</Label>
                      <Input
                        id={nameFieldId}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onBlur={(e) => void persistAssetPatch({ name: e.target.value })}
                      />
                    </div>
                    <div className="flex min-w-0 flex-col gap-1.5">
                      <Label htmlFor={detailsFieldId}>{t("assetPropertiesPanel.details")}</Label>
                      <Textarea
                        id={detailsFieldId}
                        value={editDetails}
                        onChange={(e) => setEditDetails(e.target.value)}
                        onBlur={(e) => void persistAssetPatch({ details: e.target.value })}
                      />
                    </div>
                    <CustomerClassField
                      din276Code={asset.schemaCategory}
                      value={asset.customerClass ?? null}
                      onChange={(customerClass) => void persistAssetPatch({ customerClass: customerClass ?? undefined })}
                    />
                  </div>
                </AssetCard>
                <AssetCard title={t("assetPropertiesPanel.lifecycle.title")} testId="asset-card-lifecycle">
                  <DefinitionList
                    rows={lifecycleRows(asset).map((row) => {
                      const overdue =
                        (row.key === "nextMaintenance" || row.key === "nextCheck") && isOverdue(asset[row.key]);
                      return {
                        key: row.key,
                        label: t(`assetPropertiesPanel.lifecycle.${row.key}`),
                        value: overdue ? `${row.value} (${t("assetPropertiesPanel.lifecycle.overdue")})` : row.value,
                        emphasis: row.key === "nextMaintenance" || row.key === "nextCheck",
                        tone: overdue ? ("overdue" as const) : undefined,
                      };
                    })}
                  />
                </AssetCard>
                {/* Any classified asset gets the sheet, incl. captured non-DIN extras (#1319). */}
                {asset.schema === "din276" || asset.schemaCategory ? (
                  <AssetCard
                    title={t("assetPropertiesPanel.properties")}
                    className="lg:col-span-2"
                    testId="asset-card-properties"
                  >
                    <DinPropertySheet
                      typeCode={asset.schemaCategory}
                      properties={generalProperties}
                      onChange={setGeneralProperties}
                      onPersist={persistAssetProperties}
                      showQualityState
                      showExtras
                      wide
                    />
                  </AssetCard>
                ) : null}
                {deviceType ? (
                  <AssetCard
                    title={t("assetPropertiesPanel.iotTelemetry")}
                    className="lg:col-span-2"
                    testId="asset-card-iot"
                  >
                    <IoTDeviceTypeProperties
                      deviceType={deviceType}
                      configuration={editIotConfig}
                      setConfiguration={setEditIotConfig}
                      onPersist={(config) => void persistAssetPatch({ iotConfiguration: config })}
                    ></IoTDeviceTypeProperties>
                  </AssetCard>
                ) : null}
                <AssetCard
                  title={t("assetPropertiesPanel.registration")}
                  className="lg:col-span-2"
                  testId="asset-card-registration"
                >
                  <DefinitionList
                    tiles
                    rows={[
                      { key: "schema", label: t("assetPropertiesPanel.schema"), value: asset.schema || "-" },
                      {
                        key: "schemaCategory",
                        label: t("assetPropertiesPanel.schemaCategory"),
                        value: asset.schemaCategory || "-",
                      },
                      {
                        key: "registered",
                        label: t("assetPropertiesPanel.registered"),
                        value: asset.registered ? formatAssetDateTime(asset.registered) : "-",
                      },
                      {
                        key: "registeredBy",
                        label: t("assetPropertiesPanel.registeredBy"),
                        value: asset.registeredBy || "-",
                      },
                      {
                        key: "lastModified",
                        label: t("assetPropertiesPanel.lastModified"),
                        value: asset.lastModified ? formatAssetDateTime(asset.lastModified) : "-",
                      },
                      {
                        key: "lastModifiedBy",
                        label: t("assetPropertiesPanel.lastModifiedBy"),
                        value: asset.lastModifiedBy || "-",
                      },
                    ]}
                  />
                </AssetCard>
              </div>
            </TabsContent>
            <TabsContent value="measurements" className="mt-4">
              <div className="min-w-0">
                {deviceType ? (
                  <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-4">
                      <Select
                        value={interval}
                        onValueChange={(value: string) => selectInterval(value as MeasurementRangeKey)}
                      >
                        <SelectTrigger className="w-[300px] max-w-full" aria-label={t("assetPropertiesPanel.measurements")}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-w-[calc(100vw-2rem)]">
                          {RANGE_OPTION_KEYS.map((key) => (
                            <SelectItem key={key} value={key} className="whitespace-normal">
                              {t(`assetPropertiesPanel.range.${key}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <DateField
                        disabled={interval !== "userDefined"}
                        ariaLabel={t("assetPropertiesPanel.measurements")}
                        formatDate={(date: Date | undefined) =>
                          date ? dayjs(date).format("MM/DD hh:mm") : ""
                        }
                        value={intervalStart}
                        onSelectDate={(date: Date | null | undefined) => {
                          if (date) {
                            setCustomStart(date);
                          }
                        }}
                      />
                      <DateField
                        disabled={interval !== "userDefined"}
                        ariaLabel={t("assetPropertiesPanel.measurements")}
                        formatDate={(date: Date | undefined) =>
                          date ? dayjs(date).format("MM/DD hh:mm") : ""
                        }
                        value={intervalEnd}
                        onSelectDate={(date: Date | null | undefined) => {
                          if (date) {
                            setCustomEnd(date);
                          }
                        }}
                      />
                      {measurementsLoading ? (
                        <div className="flex flex-1 items-center justify-end gap-2">
                          <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                          <span className="text-xs">
                            {t("assetPropertiesPanel.loadingMeasurements")}
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-1 flex-wrap items-center justify-end gap-4">
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs text-[color:var(--customer-primary,hsl(var(--primary)))]"
                            onClick={forceUpdate}
                          >
                            Update now
                          </Button>
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs text-[color:var(--customer-primary,hsl(var(--primary)))]"
                            onClick={
                              autoUpdateEnabled
                                ? stopAutoUpdate
                                : startAutoUpdate
                            }
                          >
                            {autoUpdateEnabled
                              ? "Stop auto-update"
                              : "Start auto-update"}
                          </Button>
                          {autoUpdateEnabled && (
                            <span className="text-xs text-muted-foreground">
                              Next update in {timeUntilNextUpdate}s
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {autoWidenedTo && (
                      <div
                        className="mt-2 text-xs text-muted-foreground"
                        data-testid="range-auto-widened"
                      >
                        {t("assetPropertiesPanel.rangeAutoWidened", {
                          range: t(`assetPropertiesPanel.range.${autoWidenedTo}`),
                        })}
                      </div>
                    )}
                    <MeasurementChartList
                      seriesList={seriesList}
                      optionsList={optionsList}
                      assetId={String(asset?.id ?? asset?._id ?? "")}
                      // Zoom belongs to the asset and the period the user picked; the computed end of
                      // a preset moves with the day and must not reset it.
                      resetKey={`${asset?._id ?? asset?.id ?? ""}|${interval}${
                        interval === "userDefined"
                          ? `|${intervalStart.getTime()}|${intervalEnd.getTime()}`
                          : ""
                      }`}
                    />
                  </div>
                ) : (
                  <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
                    {t("assetPropertiesPanel.noDeviceType")}
                  </div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="product" className="mt-4">
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center justify-between gap-4">
                  {product ? (
                    <h3 className="min-w-0 break-words text-lg font-bold leading-tight">{product.name}</h3>
                  ) : (
                    <div className="text-sm">
                      {t("assetPropertiesPanel.noProductAssociated")}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-4">
                    {product ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="gap-2"
                        onClick={clearAssetProduct}
                      >
                        <Link2Off aria-hidden="true" className="h-4 w-4" />
                        {t("assetPropertiesPanel.removeProduct")}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      className="gap-2"
                      onClick={() => {
                        setPickerFilter("");
                        setProductPickerOpen(true);
                      }}
                    >
                      {product
                        ? <RefreshCw aria-hidden="true" className="h-4 w-4" />
                        : <Plus aria-hidden="true" className="h-4 w-4" />}
                      {product
                        ? t("assetPropertiesPanel.changeProduct")
                        : t("assetPropertiesPanel.assignProduct")}
                    </Button>
                  </div>
                </div>
                {product ? (
                  <div className="mt-4">
                    <InfoList
                      valueWidth="640px"
                      list={[
                        {
                          label: t("assetPropertiesPanel.productInfo.manufacturer"),
                          value: [
                            productManufacturer?.companyName || product?.manufacturer?.companyName || "-",
                            [
                              [
                                productManufacturer?.street,
                                productManufacturer?.streetNumber,
                              ]
                                .filter(Boolean)
                                .join(" "),
                              [
                                productManufacturer?.postalCode,
                                productManufacturer?.city || product?.manufacturer?.city,
                              ]
                                .filter(Boolean)
                                .join(" "),
                              productManufacturer?.country,
                            ]
                              .filter((s) => s && String(s).trim().length > 0)
                              .join(", "),
                          ]
                            .filter((s) => s && String(s).trim().length > 0)
                            .join(" — "),
                        },
                        {
                          label: t("assetPropertiesPanel.productInfo.typ"),
                          value: product?.schemaCategory || "-",
                        },
                        ...Object.entries(product?.properties || {})
                          .map(([k, v]) => ({ label: k, value: productPropertyValue(v) }))
                          .filter(({ value }) => value.length > 0),
                      ]}
                    />
                  </div>
                ) : null}
              </div>
            </TabsContent>
            <TabsContent value="photos" className="mt-4">
              <div className="flex min-w-0 flex-col gap-4">
                <AssetPhotosEditor
                  photos={asset?.photos}
                  onPhotosChange={persistAssetPhotos}
                />
                {asset.typePlateImage ? (
                  <div className="flex min-w-0 flex-col items-start gap-2">
                    <h3 className="text-sm font-bold leading-tight">Typenschild</h3>
                    <div className="flex flex-col items-center justify-center">
                      <Zoom>
                        <div
                          role="img"
                          aria-label="Typenschild"
                          className="h-[300px] w-[200px] max-w-full rounded-md border bg-cover bg-no-repeat"
                          style={{
                            backgroundImage: `url(${userAPI.getResourceURL(
                              asset.typePlateImage,
                            )})`,
                          }}
                        />
                      </Zoom>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-1 gap-2"
                        onClick={async () => {
                          const currentAsset = assetRef.current;
                          if (!currentAsset?._id) return;
                          const savedId = currentAsset._id;
                          try {
                            const updatedAsset = { ...currentAsset };

                            delete updatedAsset.typePlateImage;

                            const updated = await updateAsset(updatedAsset);
                            if (String(viewedAssetIdRef.current) !== String(savedId)) return;
                            setAsset(updated);
                          } catch (error: any) {
                            pushError(error);
                          }
                        }}
                      >
                        <Trash2 aria-hidden="true" className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </TabsContent>
            <TabsContent value="documents" className="mt-4">
              {manufacturerView && !manufacturerEntitlements?.documents?.granted ? (
                <MarketplaceNotice
                  text={t("assetPropertiesPanel.manufacturerDocumentsLocked")}
                  offer={manufacturerEntitlements?.documents?.offer}
                />
              ) : (
                <EntityDocumentsPanel
                  entityType="Asset"
                  entity={asset}
                  onChange={(updated: any) => setAsset(updated)}
                  extraDocuments={documents}
                  onOpenExtraDocument={openProductDocument}
                  readOnly={manufacturerView}
                  fileQuery={manufacturerView ? `?asset=${asset._id}` : ""}
                />
              )}
            </TabsContent>
            <TabsContent value="partHierarchy" className="mt-4">
              <AssetPartHierarchyPanel asset={asset} onAssetUpdated={setAsset} />
            </TabsContent>
            <TabsContent value="operatorObligations" className="mt-4">
              {manufacturerView && !manufacturerEntitlements?.obligations?.granted ? (
                <MarketplaceNotice
                  text={t("assetPropertiesPanel.manufacturerObligationsLocked")}
                  offer={manufacturerEntitlements?.obligations?.offer}
                />
              ) : (
                <AssetObligationsPanel asset={asset} />
              )}
            </TabsContent>
            <TabsContent value="lifecycle" className="mt-4">
              <AssetLifecyclePanel asset={asset} formatDateTime={formatAssetDateTime} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <Dialog
        open={productPickerOpen}
        onOpenChange={(next) => {
          if (!next) setProductPickerOpen(false);
        }}
      >
        {/* 1000px wie im Bestand, auf schmalen Viewports aber innerhalb des Bildschirms. */}
        <DialogContent className="max-w-none lg:w-[1000px] w-[calc(100vw-2rem)]">
          <DialogHeader>
            <DialogTitle>{t("assetPropertiesPanel.productPicker.title")}</DialogTitle>
          </DialogHeader>
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={pickerFilterId}>
                {t("assetPropertiesPanel.productPicker.filter")}
              </Label>
              <Input
                id={pickerFilterId}
                placeholder={t("assetPropertiesPanel.productPicker.filter")}
                value={pickerFilter}
                onChange={(event) => setPickerFilter(event.target.value || "")}
              />
            </div>
            <div className="overflow-x-auto">
              <DataTable
                columns={productPickerColumns}
                data={filteredPickerProducts}
                pageSize={10}
                onRowClick={(row: any) => assignProductToAsset(row)}
                emptyMessage={t("assetPropertiesPanel.productPicker.empty")}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setProductPickerOpen(false)}>
              {t("assetPropertiesPanel.productPicker.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  ) : (
    <div className="flex min-w-0 flex-col gap-4">
      <h2 className="text-lg font-semibold leading-tight">Assets</h2>
      <AssetList></AssetList>
    </div>
  );
}
